import React, { useContext } from 'react';
import styled, { css } from 'styled-components';
import { ReactComponent as BaseClientPreferredIcon } from '@ubeya/shared-web/assets/heart-client-pink.svg';
import { ReactComponent as BaseClientBlockedIcon } from '@ubeya/shared-web/assets/blocked.svg';
import { ReactComponent as BaseCrownIcon } from '@ubeya/shared-web/assets/stats-crown.svg';
import { ReactComponent as BaseLeafIcon } from '@ubeya/shared-web/assets/stats-leaf.svg';
import { ReactComponent as BaseRookieIcon } from '@ubeya/shared-web/assets/sand-clock.svg';
import { Tooltip } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';

const statsIcon = css`
  width: 16px;
  height: 16px;
  margin-left: 1px;
  flex-shrink: 0;
`;

const ClientPreferredIcon = styled(BaseClientPreferredIcon)`
  ${statsIcon};
`;

const ClientBlockedIcon = styled(BaseClientBlockedIcon)`
  ${statsIcon};
`;

const CrownIcon = styled(BaseCrownIcon)`
  circle {
    fill: ${({ theme }) => theme.colors.yellowPartially};
  }
  ${statsIcon};
  margin-left: 0px;
`;

const LeafIcon = styled(BaseLeafIcon)`
  ${statsIcon};
  margin-left: 0px;
`;

const RookieIcon = styled(BaseRookieIcon)`
  ${statsIcon};
  margin-left: 0px;
`;

const Pill = styled.span`
  background-color: ${({ $score, $isExternal, $isLeaf, $isRookie, theme }) => {
    if ($isExternal) return 'transparent';
    if (!$score || $isLeaf || $isRookie) return theme.colors.grey2;
    if ($score < 60) return theme.colors.redLight;
    if ($score < 80) return theme.colors.orangeLight;
    return theme.colors.greenLight;
  }};
  color: ${({ theme }) => theme.colors.black};
  height: 18px;
  border-radius: 50px;
  display: flex;
  align-items: center;
  justify-content: ${({ $hasIcon }) => ($hasIcon ? 'flex-start' : 'center')};
  gap: 4px;
  margin-top: 0;
  white-space: nowrap;
  box-sizing: border-box;
  margin-bottom: 0px;
  padding: 0 1px 0 1px;
  min-width: 60px;
  ${({ $isExternal }) =>
    $isExternal &&
    css`
      width: fit-content;
    `}
`;

const getIconConfig = ({ isCrown, isLeaf, isRookie, isClientPreferred, isExternal }, t) => {
  if (isClientPreferred === true) {
    return {
      icon: <ClientPreferredIcon />,
      tooltip: t('clientPreferredTooltip')
    };
  }
  if (isClientPreferred === false) {
    return {
      icon: <ClientBlockedIcon />,
      tooltip: t('clientBlockedTooltip')
    };
  }
  if (isExternal) {
    return {
      icon: null,
      tooltip: null
    };
  }
  if (isCrown) {
    return {
      icon: <CrownIcon />,
      tooltip: t('crownTooltip')
    };
  }
  if (isLeaf) {
    return {
      icon: <LeafIcon />,
      tooltip: t('statsNewEmployee')
    };
  }
  if (isRookie) {
    return {
      icon: <RookieIcon />,
      tooltip: t('earlyScoreTooltip')
    };
  }

  return {
    icon: null,
    tooltip: null
  };
};

const Content = styled.div.attrs(() => ({ className: 'name' }))`
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: ${({ $hasIcon }) => ($hasIcon ? '1' : '0')};
  padding-right: ${({ $hasIcon }) => ($hasIcon ? '8px' : '0')};
`;

const MatchPill = ({
  match,
  isCrown = false,
  isLeaf = false,
  isRookie = false,
  isClientPreferred = undefined,
  text = '',
  isExternal,
  className,
  withTooltip = false
}) => {
  const { t } = useContext(ConfigContext);

  if (match === -1) return null;

  const iconConfig = getIconConfig({ isCrown, isLeaf, isRookie, isClientPreferred, isExternal }, t);

  const hasIcon = iconConfig.icon !== null;

  return (
    <Tooltip disabled={!withTooltip || !iconConfig.tooltip} body={iconConfig.tooltip}>
      <Pill
        $score={match}
        $isLeaf={isLeaf}
        $isRookie={isRookie}
        $isExternal={isExternal}
        $hasIcon={hasIcon}
        className={className}>
        {iconConfig.icon}
        {!isExternal && <Content $hasIcon={hasIcon}>{text || `${match}%`}</Content>}
      </Pill>
    </Tooltip>
  );
};
export default MatchPill;
